import { RelationshipStatus } from '@/models/common/RelationshipStatus';
import { ContactClassificationStatus } from '@/models/ContactClassificationStatus';

/**
 * Get the value of a CSS custom property (variable) from the document root
 */
export const getCssVar = (name: string): string =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim();

/**
 * Status color set interface for all status types
 * Uses CSS variables defined in index.css for consistency
 */
export interface StatusColorSet {
  background: string;       // Pastel background (e.g., #D1FAE5)
  backgroundAlpha: string;  // Transparent background with alpha (e.g., #10B98120)
  border: string;           // Border/main color (e.g., #10B981)
  text: string;             // Dark text color (e.g., #065F46)
}

// ============================================
// Relationship Status Colors (Children Management)
// ============================================

export const getStatusColors = (): Record<RelationshipStatus, string> => ({
  [RelationshipStatus.PendingApproval]: getCssVar('--status-pending-border'),
  [RelationshipStatus.Approved]: getCssVar('--status-approved-border'),
  [RelationshipStatus.Rejected]: getCssVar('--status-rejected-border'),
  [RelationshipStatus.PendingCompletion]: getCssVar('--status-pending-completion-border'),
  [RelationshipStatus.CompletionReturned]: getCssVar('--status-pending-completion-border'),
});

export const getStatusColorSets = (): Record<RelationshipStatus, StatusColorSet> => {
  const approvedBorder = getCssVar('--status-approved-border');
  const rejectedBorder = getCssVar('--status-rejected-border');
  const pendingBorder = getCssVar('--status-pending-border');
  const pendingCompletionBorder = getCssVar('--status-pending-completion-border');

  return {
    [RelationshipStatus.Approved]: {
      background: getCssVar('--status-approved-bg'),
      backgroundAlpha: approvedBorder + '20',
      border: approvedBorder,
      text: getCssVar('--status-approved-text'),
    },
    [RelationshipStatus.Rejected]: {
      background: getCssVar('--status-rejected-bg'),
      backgroundAlpha: rejectedBorder + '20',
      border: rejectedBorder,
      text: getCssVar('--status-rejected-text'),
    },
    [RelationshipStatus.PendingApproval]: {
      background: getCssVar('--status-pending-bg'),
      backgroundAlpha: pendingBorder + '20',
      border: pendingBorder,
      text: getCssVar('--status-pending-text'),
    },
    [RelationshipStatus.PendingCompletion]: {
      background: getCssVar('--status-pending-completion-bg'),
      backgroundAlpha: pendingCompletionBorder + '20',
      border: pendingCompletionBorder,
      text: getCssVar('--status-pending-completion-text'),
    },
    [RelationshipStatus.CompletionReturned]: {
      background: getCssVar('--status-pending-completion-bg'),
      backgroundAlpha: pendingCompletionBorder + '20',
      border: pendingCompletionBorder,
      text: getCssVar('--status-pending-completion-text'),
    },
  };
};

// ============================================
// Classification Status Colors (Classifications Management)
// ============================================

export const getClassificationStatusColors = (): Record<ContactClassificationStatus, string> => ({
  [ContactClassificationStatus.PendingApproval]: getCssVar('--status-pending-border'),
  [ContactClassificationStatus.PendingCompletion]: getCssVar('--status-pending-completion-border'),
  [ContactClassificationStatus.Approved]: getCssVar('--status-approved-border'),
  [ContactClassificationStatus.Rejected]: getCssVar('--status-rejected-border'),
  [ContactClassificationStatus.Cancelled]: getCssVar('--status-cancelled-border'),
  [ContactClassificationStatus.CompletionReturned]: getCssVar('--status-pending-completion-border'),
});

export const getClassificationStatusColorSets = (): Record<ContactClassificationStatus, StatusColorSet> => {
  const approvedBorder = getCssVar('--status-approved-border');
  const rejectedBorder = getCssVar('--status-rejected-border');
  const pendingBorder = getCssVar('--status-pending-border');
  const pendingCompletionBorder = getCssVar('--status-pending-completion-border');
  const cancelledBorder = getCssVar('--status-cancelled-border');

  return {
    [ContactClassificationStatus.Approved]: {
      background: getCssVar('--status-approved-bg'),
      backgroundAlpha: approvedBorder + '20',
      border: approvedBorder,
      text: getCssVar('--status-approved-text'),
    },
    [ContactClassificationStatus.Rejected]: {
      background: getCssVar('--status-rejected-bg'),
      backgroundAlpha: rejectedBorder + '20',
      border: rejectedBorder,
      text: getCssVar('--status-rejected-text'),
    },
    [ContactClassificationStatus.PendingApproval]: {
      background: getCssVar('--status-pending-bg'),
      backgroundAlpha: pendingBorder + '20',
      border: pendingBorder,
      text: getCssVar('--status-pending-text'),
    },
    [ContactClassificationStatus.PendingCompletion]: {
      background: getCssVar('--status-pending-completion-bg'),
      backgroundAlpha: pendingCompletionBorder + '20',
      border: pendingCompletionBorder,
      text: getCssVar('--status-pending-completion-text'),
    },
    [ContactClassificationStatus.Cancelled]: {
      background: getCssVar('--status-cancelled-bg'),
      backgroundAlpha: cancelledBorder + '20',
      border: cancelledBorder,
      text: getCssVar('--status-cancelled-text'),
    },
    [ContactClassificationStatus.CompletionReturned]: {
      background: getCssVar('--status-pending-completion-bg'),
      backgroundAlpha: pendingCompletionBorder + '20',
      border: pendingCompletionBorder,
      text: getCssVar('--status-pending-completion-text'),
    },
  };
};

